import { FilterList } from '@mui/icons-material'
import { Box, Checkbox, Chip, Menu, MenuItem, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useGetCustomerLocalities } from 'src/api/localities'
import {
  invalidateMaintenanceTemplateCache,
  useAssignMaintenanceTemplate,
  useGetLocalityMaintenanceTemplates,
} from 'src/api/maintenanceTemplate'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { StyledCard } from 'src/components/GenericCardMinimalVersion'
import { MainButton } from 'src/components/MainButton'
import GenericTable from 'src/components/table/GenericTable'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import LocalityAssignToMaintenanceTemplateForm, {
  PopOverActiveLocality,
} from './LocalityAssignToMaintenanceTemplateForm'
import MaintenanceAssignCheckBox from './MaintenanceAssignCheckBox'

const MaintenanceActiveLocalities = () => {
  const { t } = useTranslation(['maintenance', 'common'])
  const { id } = useParams()

  const { data: localities, isLoading: isLocalityLoading } = useGetCustomerLocalities({ clientId: id })
  const { data: maintenanceTemplates } = useGetLocalityMaintenanceTemplates(id as EntityId)
  const { mutateAsync } = useAssignMaintenanceTemplate(id as EntityId)

  const [selectedData, setSelectedData] = useState<{ localityId: number; maintenanceTemplateId: number } | undefined>()
  const [filteredLocalityList, setFilteredLocalityList] = useState<{ id: number; name: string }[]>([])
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [confirmAnchorEl, setConfirmAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null)
  const menuOpen = Boolean(menuAnchorEl)
  const deleteConfirmationOpen = Boolean(confirmAnchorEl)

  const filteredLocalities = useMemo(() => {
    if (filteredLocalityList.length > 0) {
      return localities?.filter(locality => filteredLocalityList.find(l => l.id === locality.id) != null)
    }
    return localities
  }, [localities, filteredLocalityList])

  const columns: GridColumns = useMemo(() => {
    const columnArray: GridColumns = [
      {
        field: 'name',
        headerName: t('locality', { ns: 'common' }),
        flex: 1,
        minWidth: 150,
      },
    ]
    maintenanceTemplates?.map(maintenanceTemplate => {
      columnArray.push({
        field: maintenanceTemplate.id.toString(),
        headerName: maintenanceTemplate.name,
        flex: 1,
        minWidth: 150,
        headerAlign: 'center',
        renderCell: params => {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <MaintenanceAssignCheckBox
                onUncheck={anchorEl => {
                  setSelectedData({ localityId: params.row.id, maintenanceTemplateId: maintenanceTemplate.id })
                  setConfirmAnchorEl(anchorEl)
                }}
                onCheck={anchorEl => {
                  setSelectedData({ localityId: params.row.id, maintenanceTemplateId: maintenanceTemplate.id })
                  setAnchorEl(anchorEl)
                }}
                defaultChecked={
                  maintenanceTemplate.localityMaintenanceTemplates?.some(s => s.localityId === params.row.id) || false
                }
              />
            </Box>
          )
        },
      })
    })
    return columnArray
  }, [maintenanceTemplates])

  const onAssignLocality = useCallback(
    async (data: any) => {
      await mutateAsync({
        localityId: data?.localityId as number,
        maintenanceTemplateId: data?.maintenanceTemplateId as number,
        isChecked: true,
        startDate: data.startDate,
        endDate: data.endDate,
      })
    },
    [selectedData, id],
  )

  const isScrollable = (filteredLocalities?.length ?? 0) > 9

  return (
    <CommonPageLayout
      titleSection={{
        titleComponent: (
          <Flex.Row>
            <Typography color={theme.palette.primaryMain[theme.palette.mode]} variant="h5" fontWeight={600}>
              {t('active-localities', { ns: 'maintenance' })}
            </Typography>
            <Flex.Row flexWrap={'wrap'}>
              {filteredLocalityList.length > 0 &&
                filteredLocalityList?.map(locality => (
                  <Padding.p2 key={locality.id}>
                    <Chip
                      label={locality.name}
                      onDelete={() => {
                        setFilteredLocalityList(prev => prev.filter(l => l.id !== locality.id))
                      }}
                      sx={{
                        marginLeft: 1,
                        backgroundColor: theme.palette.primaryMain[theme.palette.mode],
                        color: theme.palette.common.white,
                        '& .MuiChip-deleteIcon': {
                          fill: theme.palette.common.white,
                        },
                      }}
                    />
                  </Padding.p2>
                ))}
            </Flex.Row>
          </Flex.Row>
        ),
      }}
      topAction={[
        <GridActionButton
          key={'filter'}
          buttonText={t('filter', { ns: 'common' })}
          startIcon={<FilterList />}
          onClick={event => {
            setMenuAnchorEl(event.currentTarget)
          }}
        />,
      ]}
    >
      <StyledCard sx={{ padding: '12px 8px' }}>
        <GenericTable
          key={isScrollable ? 'scrollable' : 'auto-height'}
          columns={columns}
          getRowHeight={() => 44}
          height={isScrollable ? 445 : undefined}
          autoHeight={!isScrollable}
          data={filteredLocalities}
          loading={isLocalityLoading}
          id={'maintenance-active-localities-table'}
          initialState={{ pinnedColumns: { left: ['name'] } }}
          allowHorizontalScroll={true}
        />
      </StyledCard>
      <LocalityAssignToMaintenanceTemplateForm
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        onSubmitAssignData={async data => await onAssignLocality(data)}
        selectedData={selectedData}
        customerId={id as EntityId}
      />
      <Menu
        id="basic-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={() => setMenuAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {localities?.map(locality => (
          <MenuItem
            key={locality.id}
            onClick={() => {
              setFilteredLocalityList(prev => {
                if (prev.find(l => l.id === locality.id) != null) {
                  return prev.filter(l => l.id !== locality.id)
                } else {
                  return [...prev, { id: locality.id, name: locality.name }]
                }
              })
            }}
          >
            <Checkbox checked={filteredLocalityList.find(l => l.id === locality.id) != null} />
            {locality.name}
          </MenuItem>
        ))}
      </Menu>
      <PopOverActiveLocality
        open={deleteConfirmationOpen}
        anchorEl={confirmAnchorEl}
        onClose={() => {
          setConfirmAnchorEl(null)
        }}
      >
        <Padding.p14 width={270}>
          <Flex.Row justifyContent={'space-between'} paddingLeft={'8px'}>
            <Typography variant="body2" sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }}>
              {t('delete-confirm-locality-description', { ns: 'locality-group' })}
            </Typography>
          </Flex.Row>
          <Flex.Row justifyContent={'flex-end'} marginTop={2}>
            <Padding.pr8>
              <MainButton
                variant="contained"
                sx={{
                  color: 'white',
                  backgroundColor: getThemeColor(theme, MUIThemeColors.errorMain),
                  '&:hover': {
                    backgroundColor: getThemeColor(theme, MUIThemeColors.errorDark),
                  },
                }}
                onClick={async () => {
                  if (selectedData) {
                    await mutateAsync({
                      localityId: selectedData.localityId,
                      maintenanceTemplateId: selectedData.maintenanceTemplateId,
                      isChecked: false,
                    })
                    invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(id as EntityId)
                    invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(
                      id as EntityId,
                      selectedData.localityId,
                    )
                    toast.success(t('unassign-success', { ns: 'maintenance' }))
                  }
                  setConfirmAnchorEl(null)
                }}
              >
                {t('yes', { ns: 'common' })}
              </MainButton>
            </Padding.pr8>
            <GridActionButton
              buttonText={t('no', { ns: 'common' })}
              onClick={() => {
                setConfirmAnchorEl(null)
              }}
            />
          </Flex.Row>
        </Padding.p14>
      </PopOverActiveLocality>
    </CommonPageLayout>
  )
}

export default MaintenanceActiveLocalities
